// Per-workflow operate/schedule settings (Figma "7. Settings" — Configure +
// Limits tabs, and the library's Active toggle / "Active Every" column).
//
// SERVER-BACKED with a localStorage cache: reads stay synchronous (the panels
// read at render time) against the cache; `hydrateWorkflowSettings` pulls the
// server row into the cache when a settings surface mounts, and
// `updateWorkflowSettings` writes through to the server best-effort. The
// server row (workflow_settings) is what the scheduler and run budgets read —
// the cache only serves this browser's rendering.

import { AGENT_ALIASES, AgentId } from '@/constants/agentNames'

export type KnowledgeFileEntry = string | { name: string; documentId?: string }

/** Notification recipient. Legacy entries are free-text names (no email —
 *  never emailed); the picker stores {name, email} from the tenant's users. */
export type NotifyPersonEntry = string | { name: string; email?: string }

/** Display name of a notification recipient regardless of shape. */
export function notifyPersonName(entry: NotifyPersonEntry): string {
  return typeof entry === 'string' ? entry : entry.name
}

/** Display name of a knowledge entry regardless of shape. */
export function knowledgeFileName(entry: KnowledgeFileEntry): string {
  return typeof entry === 'string' ? entry : entry.name
}

export interface WorkflowLimitSettings {
  /** Monthly whole-workflow spend cap, USD. Workflow turns off when hit. */
  workflowSpendUsd: number | null
  /** Alert thresholds for monthly usage, USD. */
  notifyAtUsd: number[]
  /** Per-run spend cap, USD. */
  perRunSpendUsd: number | null
}

/**
 * Run-time review posture (Figma "10. Update" — Configure-tab Autonomy):
 * how often a run pauses for human approval. Mirrors the backend enum.
 */
export const WORKFLOW_AUTONOMY_LEVELS = [
  'review_every_step',
  'review_critical',
  'dangerously_bypass',
] as const
export type WorkflowAutonomy = (typeof WORKFLOW_AUTONOMY_LEVELS)[number]

/** Server value → known level; unknown strings (newer server enum) read as the default. */
function autonomyOrDefault(value: unknown): WorkflowAutonomy {
  return WORKFLOW_AUTONOMY_LEVELS.includes(value as WorkflowAutonomy)
    ? (value as WorkflowAutonomy)
    : DEFAULT_WORKFLOW_SETTINGS.autonomy
}

export interface WorkflowSettings {
  /** Library "Active" toggle — whether the schedule is on. Config-only until the scheduler lands. */
  active: boolean
  /** How often the workflow runs when active (e.g. '1 hour' | '1 day' | '1 week'). */
  frequency: string
  /** Local time-of-day for scheduled runs, 'HH:MM'. */
  time: string
  /** Step budget the AI must plan within (maps to the run budget). */
  maxSteps: number
  /** Run-time review posture (Autonomy dropdown); governs the review gate. */
  autonomy: WorkflowAutonomy
  /** Overall instruction the workflow is generated from (Configure tab). */
  instructions: string
  /** Display names of the AI engineers selected to execute this workflow. */
  engineers: string[]
  /** Knowledge files attached to the workflow. Legacy entries are bare name
   *  strings (dummy upload tab); Data Catalog attachments carry the
   *  documentId so runs scope retrieval to them. */
  knowledgeFiles: KnowledgeFileEntry[]
  /** Run-event email recipients ({name, email}); legacy bare names are inert. */
  notifyPeople: NotifyPersonEntry[]
  limits: WorkflowLimitSettings
  /**
   * Per-step on-error policy chosen in the node inspector
   * ('stop' | 'continue' | 'error_path'), keyed by step id. Config-only until
   * the engine grows author-configurable error paths.
   */
  onErrorByStep: Record<string, string>
  /**
   * Per-step HITL override (false = the step never pauses on a
   * clarification; it proceeds unattended with honest unknowns).
   */
  hitlByStep: Record<string, boolean>
}

/**
 * The persona that actually drives workflow runs today, auto-selected whenever
 * a workflow has no explicit engineer selection (fresh workflow, or a server
 * row saved before this field existed) — mirrors buildExecutors' fallback in
 * workflowData.ts.
 */
export const DEFAULT_ENGINEER =
  AGENT_ALIASES[AgentId.PLANT_SUPPORT]?.displayName ?? 'AI Plant Support Engineer'

/** Fresh copy of `list`, or the default persona when nothing is selected. */
function engineersOrDefault(list: string[] | undefined): string[] {
  return list?.length ? [...list] : [DEFAULT_ENGINEER]
}

export const DEFAULT_WORKFLOW_SETTINGS: WorkflowSettings = {
  active: false,
  frequency: '1 day',
  time: '08:00',
  // Default per the design walkthrough: ~20–25 steps keeps a run under ~10min.
  maxSteps: 25,
  // Opt-in, matching DEFAULT_WORKFLOW_AUTONOMY on the server (FS item 3).
  autonomy: 'review_critical',
  instructions: '',
  engineers: [DEFAULT_ENGINEER],
  knowledgeFiles: [],
  notifyPeople: [],
  limits: { workflowSpendUsd: null, notifyAtUsd: [], perRunSpendUsd: null },
  onErrorByStep: {},
  hitlByStep: {},
}

const STORAGE_KEY = 'workflow-settings-v1'

type SettingsMap = Record<string, Partial<WorkflowSettings>>

function readAll(): SettingsMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : {}
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
      ? (parsed as SettingsMap)
      : {}
  } catch {
    return {}
  }
}

function writeAll(map: SettingsMap): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map))
  } catch {
    // Storage full/blocked — settings simply don't persist this session.
  }
}

/** Settings for a workflow id, merged over defaults (missing keys stay default). */
export function getWorkflowSettings(workflowId: string): WorkflowSettings {
  const saved = readAll()[workflowId] ?? {}
  // Fresh copies of every nested value (arrays included) so callers can't
  // mutate the shared defaults through a returned reference.
  return {
    ...DEFAULT_WORKFLOW_SETTINGS,
    ...saved,
    engineers: engineersOrDefault(saved.engineers),
    knowledgeFiles: [...(saved.knowledgeFiles ?? DEFAULT_WORKFLOW_SETTINGS.knowledgeFiles)],
    notifyPeople: [...(saved.notifyPeople ?? DEFAULT_WORKFLOW_SETTINGS.notifyPeople)],
    onErrorByStep: { ...(saved.onErrorByStep ?? DEFAULT_WORKFLOW_SETTINGS.onErrorByStep) },
    hitlByStep: { ...(saved.hitlByStep ?? DEFAULT_WORKFLOW_SETTINGS.hitlByStep) },
    limits: {
      ...DEFAULT_WORKFLOW_SETTINGS.limits,
      ...(saved.limits ?? {}),
      notifyAtUsd: [
        ...((saved.limits?.notifyAtUsd ?? DEFAULT_WORKFLOW_SETTINGS.limits.notifyAtUsd)),
      ],
    },
  }
}

/**
 * A settings patch: any top-level field, and PARTIAL limits (so callers can
 * patch one cap without restating the rest — the merge below fills them in).
 */
export type WorkflowSettingsPatch = Partial<Omit<WorkflowSettings, 'limits'>> & {
  limits?: Partial<WorkflowLimitSettings>
}

// Change listeners, so settings-reading panels that are ALREADY MOUNTED when
// another surface writes (e.g. the node inspector's Files list while the
// Select-files dialog attaches one) re-read instead of rendering stale values.
type SettingsListener = (workflowId: string) => void
const settingsListeners = new Set<SettingsListener>()

/** Subscribe to settings writes. Returns the unsubscribe function. */
export function subscribeWorkflowSettings(listener: SettingsListener): () => void {
  settingsListeners.add(listener)
  return () => settingsListeners.delete(listener)
}

export function notifySettingsChanged(workflowId: string): void {
  settingsListeners.forEach((listener) => listener(workflowId))
}

/** Merge a partial update into a workflow's settings and persist. Returns the new value. */
export function updateWorkflowSettings(
  workflowId: string,
  patch: WorkflowSettingsPatch,
): WorkflowSettings {
  const all = readAll()
  const current = getWorkflowSettings(workflowId)
  const next = {
    ...current,
    ...patch,
    // Deep-merge limits, copying the array so the stored value can't be
    // mutated later through the caller's reference.
    limits: {
      ...current.limits,
      ...(patch.limits ?? {}),
      ...(patch.limits?.notifyAtUsd ? { notifyAtUsd: [...patch.limits.notifyAtUsd] } : {}),
    },
  }
  all[workflowId] = next
  writeAll(all)
  notifySettingsChanged(workflowId)
  // Write-through to the server row (the scheduler/budget source of truth).
  // Best-effort: a failed PUT leaves the local cache serving this browser;
  // the next hydrate reconciles.
  void pushWorkflowSettings(workflowId, patch)
  return next
}

/** Server row shape (see workflowApi.WorkflowSettingsRow). */
interface ServerSettingsRow {
  active: boolean
  frequency: string
  timeOfDay: string
  maxSteps: number
  autonomy?: WorkflowAutonomy
  instructions: string
  engineers: string[]
  knowledgeFiles: KnowledgeFileEntry[]
  notifyPeople: NotifyPersonEntry[]
  limits: { workflowSpendUsd?: number | null; notifyAtUsd?: number[]; perRunSpendUsd?: number | null }
  onErrorByStep: Record<string, string>
  hitlByStep?: Record<string, boolean>
}

/** Server row → the FE settings shape (timeOfDay ↔ time naming). Pure; tested. */
export function settingsFromServer(row: ServerSettingsRow): WorkflowSettings {
  return {
    active: row.active,
    frequency: row.frequency,
    time: row.timeOfDay,
    maxSteps: row.maxSteps,
    autonomy: autonomyOrDefault(row.autonomy),
    instructions: row.instructions,
    // Empty server rows (fresh workflow / pre-field row) read as the default
    // persona, so the panel never shows "no engineer" for a runnable workflow.
    engineers: engineersOrDefault(row.engineers),
    knowledgeFiles: row.knowledgeFiles ?? [],
    notifyPeople: row.notifyPeople ?? [],
    limits: {
      workflowSpendUsd: row.limits?.workflowSpendUsd ?? null,
      notifyAtUsd: row.limits?.notifyAtUsd ?? [],
      perRunSpendUsd: row.limits?.perRunSpendUsd ?? null,
    },
    onErrorByStep: row.onErrorByStep ?? {},
    hitlByStep: row.hitlByStep ?? {},
  }
}

/** FE patch → the server PUT body (only the fields present). Pure; tested. */
export function settingsToServerPatch(patch: WorkflowSettingsPatch): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  if (patch.active !== undefined) out.active = patch.active
  if (patch.frequency !== undefined) out.frequency = patch.frequency
  if (patch.time !== undefined) out.timeOfDay = patch.time
  if (patch.maxSteps !== undefined) out.maxSteps = patch.maxSteps
  if (patch.autonomy !== undefined) out.autonomy = patch.autonomy
  if (patch.instructions !== undefined) out.instructions = patch.instructions
  if (patch.engineers !== undefined) out.engineers = patch.engineers
  if (patch.knowledgeFiles !== undefined) out.knowledgeFiles = patch.knowledgeFiles
  if (patch.notifyPeople !== undefined) out.notifyPeople = patch.notifyPeople
  if (patch.limits !== undefined) out.limits = patch.limits
  if (patch.onErrorByStep !== undefined) out.onErrorByStep = patch.onErrorByStep
  if (patch.hitlByStep !== undefined) out.hitlByStep = patch.hitlByStep
  return out
}

async function pushWorkflowSettings(
  workflowId: string,
  patch: WorkflowSettingsPatch,
): Promise<void> {
  // The "new workflow" bucket has no server identity to persist against.
  if (!workflowId || workflowId === 'workflow-new') return
  const body = settingsToServerPatch(patch)
  if (Object.keys(body).length === 0) return
  try {
    const { updateWorkflowTemplateSettings } = await import('./workflowApi')
    await updateWorkflowTemplateSettings(workflowId, body)
  } catch {
    // Offline / flag off — the local cache still serves this browser.
  }
}

/**
 * Pull the server settings row into the local cache (and return it). Call when
 * a settings-reading surface mounts so panels render server truth; falls back
 * to the cached/local value when the API is unavailable.
 */
export async function hydrateWorkflowSettings(workflowId: string): Promise<WorkflowSettings> {
  if (!workflowId || workflowId === 'workflow-new') return getWorkflowSettings(workflowId)
  try {
    const { getWorkflowTemplateSettings } = await import('./workflowApi')
    const row = await getWorkflowTemplateSettings(workflowId)
    const next = settingsFromServer(row)
    const all = readAll()
    all[workflowId] = next
    writeAll(all)
    // Server truth just replaced the cache — mounted panels re-read it.
    notifySettingsChanged(workflowId)
    return next
  } catch {
    return getWorkflowSettings(workflowId)
  }
}

/**
 * Human label for the library "Active Every" column, e.g. 'Every 24hr'.
 * Mirrors the design's copy for the known frequencies; falls back to the raw value.
 */
export function frequencyLabel(frequency: string): string {
  switch (frequency) {
    case '1 hour':
      return 'Every 1hr'
    case '12 hours':
      return 'Every 12hr'
    case '1 day':
      return 'Every 24hr'
    case '1 week':
      return 'Every week'
    default:
      return frequency ? `Every ${frequency}` : '—'
  }
}
